import { DateTime } from "luxon";

import type {
  ApiDailyReadMetadata,
  DailyReadFeedback,
  DailyReadMetadata,
} from "@app/analytics/types/dailyReadTypes";
import { get, getBlobUrl, patch } from "@app/common/api";
import { isResponseErrorWithStatus, splitParagraphs } from "@app/common/utils";

const parseMetadata = (metadata: ApiDailyReadMetadata) => {
  const { localDate, text, ...rest } = metadata;
  return {
    ...rest,
    date: DateTime.fromFormat(localDate, "yyyy-MM-dd"),
    transcript: splitParagraphs(text),
  };
};

export const getAudioUrl = (id: DailyReadMetadata["id"]) => {
  return getBlobUrl(`/pulse/daily-read-audio/${id}`, { withCreds: true });
};

export const getById = async (id: string): Promise<DailyReadMetadata> => {
  const metadataResponse = await get(`/pulse/daily-read-metadata/${id}`, {
    withCreds: true,
  });

  return parseMetadata(metadataResponse);
};

export const getLatest = async () => {
  try {
    const response = await getById("latest");
    const startOfToday = DateTime.now().startOf("day");
    const startOfResponseDate = response.date.startOf("day");

    // "latest" is stale if it was for any day prior to "today"
    const isStale =
      !startOfToday.equals(startOfResponseDate) &&
      startOfToday > startOfResponseDate;

    return {
      ...response,
      isStale,
    };
  } catch (error) {
    if (isResponseErrorWithStatus(error, 404)) {
      return null;
    }
    throw error;
  }
};

export const submitFeedback = (id: string, feedback: DailyReadFeedback) => {
  return patch(
    `/pulse/daily-read-metadata/${id}/feedback/me`,
    { feedback },
    { withCreds: true }
  );
};
